import React, { useEffect, useRef } from 'react';
import './Article.scss';
import { gsap } from './gsap';
import { createPopper } from '@popperjs/core';
import Hero from './Components/Hero/Hero';

import Header from './Components/common-header/Header';
import Footer from './Components/common-footer/Footer';
import InlinePhoto from './Components/Photos/InlinePhoto';
import FullWidthPhoto from './Components/Photos/FullWidthPhoto';
import Chapter1 from './Components/Chapters/Chapter1';
import Chapter2 from './Components/Chapters/Chapter2';
import Chapter3 from './Components/Chapters/Chapter3';
import Chapter4 from './Components/Chapters/Chapter4';
import Chapter5 from './Components/Chapters/Chapter5';
import Chapter6 from './Components/Chapters/Chapter6';
import Chapter7 from './Components/Chapters/Chapter7';
import Chapter8 from './Components/Chapters/Chapter8';
import PullQuoteWImg from './Components/PullQuote/PullQuoteWImg';
import InlineVideo from './Components/Photos/InlineVideo';
// import FullWidthVideo from './Components/Photos/FullWidthVideo';
import contents from './contents';
import RelatedContents from './Components/RelatedContents/RelatedContents';
// import Reference from './Components/Reference/Reference';
import CtaButton from './Components/CtaButton/CtaButton';

//for fullWidth Photos
import fullbleedPhoto1 from './Assets/fullbleedPhoto1.jpg';
import fullbleedPhoto2 from './Assets/fullbleedPhoto2.jpg';
import fullBleedPhoto4 from './Assets/fullbleedPhoto4.jpg';
import fullbleedPhoto5 from './Assets/fullbleedPhoto5.jpg';

//pullQuotePhotos
import pqImg1 from './Assets/pqImg1.jpg';
import pqImg2 from './Assets/pqImg2.jpg';

function Article({ heroBackgroundType, inlinePhotoSize, logoPlacement }) {
  //below states are for the audio player placement. See README for detail.

  //create refs for photos here if you want to add scale animation on scroll
  const inline1 = useRef();
  const inline2 = useRef();
  const inline3 = useRef();
  const inline4 = useRef();
  const inline5 = useRef();
  const inline6 = useRef();
  const inline7 = useRef();
  const allPhotosRef = useRef();
  allPhotosRef.current = [inline1, inline2, inline3, inline4, inline5, inline6, inline7]; // put all photo refs in this array

  const screenSizeRef = useRef(); //

  const photoSize = inlinePhotoSize !== undefined ? inlinePhotoSize : contents.inlinePhotos.size;

  //creates/shows tooltip element when mouse enters
  const handleMouseEnterTooltip = (e, currentRef) => {
    const content = currentRef.current;
    createPopper(e.target, content, {
      placement: 'bottom',
      modifiers: [
        {
          name: 'offset',
          options: {
            offset: [0, 5]
          }
        }
      ]
    });
    content.style.visibility = 'visible';
  };

  //hides tooltip element when mouse leaves
  const handleMouseLeaveTooltip = (currentRef) => {
    currentRef.current.style.visibility = 'hidden';
  };

  useEffect(() => {
    allPhotosRef.current.forEach((photo) => {
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: photo.current,
          start: '20% bottom',
          end: 'top center',
          scrub: 1
        }
      });
      tl.to(photo.current, {
        width: '100%',
        height: '100%',
        duration: 3
      });
    });
  }, []);

  return (
    <div className="article">
      <div className="height" ref={screenSizeRef} />
      <Header clientName={contents.header} />
      <Hero
        handleMouseEnterTooltip={handleMouseEnterTooltip}
        handleMouseLeaveTooltip={handleMouseLeaveTooltip}
        heroBackgroundType={heroBackgroundType}
        logoPlacement={logoPlacement}
      />
      <Chapter1 />
      <InlineVideo
        vidLink={
          'https://courageous-cdn-media-bucket.s3.amazonaws.com/intelWonderful/graphicOrbs.mp4'
        }
        vidLinkM={
          'https://courageous-cdn-media-bucket.s3.amazonaws.com/intelWonderful/graphicOrbsM.mp4'
        }
        caption={
          'A forthcoming high-tech development, Miller’s Landing, boasts frictionless connectivity while retaining the area’s natural charm. Restaurant reservations, work solutions, and even hospital visits can be managed from a smartphone thanks to Intel’s networking capabilities. '
        }
      />

      {/* <PullQuoteWImg
        quote={"Intel called us. And they said, 'We’ve got to do something here.'"}
        author={'David Hartshorn,'}
        descrip={'CEO, Geeks Without Frontiers'}
        photo={pqImg1}
      /> */}
      <Chapter2 />
      <PullQuoteWImg
        quote={
          'Basically, a whole micro-city has to be connected from different aspects—a hospitality space, a retail space, healthcare spaces, and outdoor spaces.'
        }
        author={'Zana Ilhan'}
        descrip={'Intel Global Hospitality and Entertainment Lead at Network and Edge Group'}
        photo={pqImg2}
      />
      <FullWidthPhoto
        photo={fullbleedPhoto1}
        photoM={fullbleedPhoto1}
        alt={'containers'}
        caption={
          'A rendering of the live-work project shows Miller’s Landing’s intended community atmosphere and the beautiful Colorado backdrop. SmartTown connectivity will be available across the 65-acre grounds, but future-forward tech won’t drive the building aesthetic; the area’s frontier town history will be reflected in the design.'
        }
      />
      <Chapter3 />
      <PullQuoteWImg
        quote={
          'Intel is building a coalition of ecosystem partners behind the scenes to marry the IT to the OT for a fully integrated technology tech stack to deliver these abilities.'
        }
        author={'Ken Wilkinson,'}
        descrip={'Managing Partner of collaborating tech solutions company Layer 10'}
        photo={pqImg1}
      />
      <Chapter4 />
      <FullWidthPhoto
        photo={fullbleedPhoto2}
        photoM={fullbleedPhoto2}
        alt={'map of castle rock'}
        caption={
          'P3 Advisors LLC identified the Miller’s Landing property and removed the town landfill, preparing the land for this unique project. “We created a shared vision together with our private partners including our public partner the Town of Castle Rock,” says P3 Managing Director Shawn Temple. The site is perfectly situated between downtown Castle Rock and the expansive Philip S. Miller Park. Proximity to the I-25 freeway is also an asset to the project.'
        }
      />

      <Chapter5 />
      <InlineVideo
        vidLink={
          'https://courageous-cdn-media-bucket.s3.amazonaws.com/intelWonderful/bulletInfo.mp4'
        }
        vidLinkM={
          'https://courageous-cdn-media-bucket.s3.amazonaws.com/intelWonderful/bulletInfoM.mp4'
        }
        caption={
          'Daily conveniences can be arranged with a simple screen tap for residents and visitors, from ordering delivery to requesting transportation. Intel’s industry-leading edge technology is behind the frictionless mobile connection that makes Miller’s Landing so unique.'
        }
      />

      <Chapter6 />

      <Chapter7 />
      <FullWidthPhoto
        photo={fullbleedPhoto5}
        photoM={fullbleedPhoto5}
        alt={'trees'}
        caption={
          'One of the biggest promises and opportunities of a SmartTown such as Miller’s Landing lies in its eco-friendliness. Everything is connected and monitored, so every home and building only uses energy as needed. That includes heating and cooling, lighting, window shades, and home entertainment. Even across these formerly disparate systems, power consumption is fine-tuned and optimized.'
        }
      />
      <Chapter8 />
      <FullWidthPhoto
        photo={fullBleedPhoto4}
        photoM={fullBleedPhoto4}
        alt={'happy people'}
        caption={
          '(Left) Quality foods are a focus of the Miller’s Landing culinary plan and smart screen usage and frictionless connectivity makes transactions a breeze. Customers will enjoy seamless hospitality and entertainment experiences on the property. (Right) Outdoor spaces and events are central to the Miller’s Landing vision, both on campus and next door; the family-friendly play areas of Philip S. Miller Park are a big draw for locals. Whether it’s sliding, hiking, zip-lining, or just working outdoors, residents and visitors will want to take advantage of the area’s 300 days of sunshine.'
        }
      />

      <CtaButton
        handleMouseEnterTooltip={handleMouseEnterTooltip}
        handleMouseLeaveTooltip={handleMouseLeaveTooltip}
      />
      {/* <div className="article__divider" /> */}
      {/* <Reference /> */}
      <RelatedContents />
      <Footer />
    </div>
  );
}

export default Article;
